// The electric interaction — the signature (product-canon file 94 §2, AS
// CORRECTED 2026-11-02).
//
// The reference is THE ELECTRIC COMPANY (the show), NOT electricity as
// current-in-a-wire. The earlier reading — a tie as a wire with a hot charge
// packet running along it — was the wrong metaphor and is RETIRED. The real
// signature is the show's "Silhouettes" segment: two people/mouths facing each
// other from the two sides, each EMITTING part of a word, and the two parts
// travel inward and MEET IN THE MIDDLE to form the whole.
//
// That is Objexiv's connection, exactly: a connection is two people, from both
// sides, emitting their words (their takes), which converge and MEET to form the
// shared thing between them — the tie, the agreement, the reading. The signal is
// not abstract current decorating a line; it is words coming out of people's
// mouths and meeting. The "line" is just the path the two emissions travel to
// meet.
//
// What this module draws (canon 94 §2 corrected, "best not simplest"):
//   1. A TIE forming is TWO-SIDED EMISSION. Both endpoints are people/their
//      takes. Each emits its words from its side — a stylised mouth/voice cue at
//      each end, a lit emission travelling inward along its half of the path —
//      and they MEET IN THE MIDDLE, where the tie's shared glow settles. Not one
//      node energizing a passive far node: BOTH sides speak, and the meeting is
//      the connection.
//   2. A TAKE answering a CHALLENGE: the person emits their words from their
//      side; the emission travels to the challenge and lands as a lit point on
//      the rim — the challenge's question and the person's answer meeting.
//   3. LIVENESS is this emission happening now: voices/words in motion while
//      live, settling to a quiet lit mark at rest (the met point). Weight of the
//      settled tie stays its only state (file 77); liveness is a transient the
//      island paints and clears.
//   4. One loud charge colour (--live / --live-hot) colours the emission; the six
//      hands stay the identity of WHO is speaking.
//   5. BEST rendering: a real SVG emission glow (feGaussianBlur) the lit elements
//      reference, layered light, not a flat drop-shadow.
//
// No hand / body / finger / cursor is ever drawn (file 83 §1.4). The mouth/voice
// cue is a STYLISED crisp-geometry mark of a voice at each side — a small opening
// chevron/arc that reads as "a voice speaking toward the middle" — never a
// literal illustrated figure or face.
//
// All motion respects prefers-reduced-motion: it settles to the met, lit-but-
// still frame (everything that would be lit *is* lit at the meeting point,
// nothing moves). SSR / test safe (no document access at import time).
//
// Token contract. We consume the CSS custom properties the token layer
// (web/css/tokens.css) already defines — --live, --live-hot, --ink-quiet,
// --ground, the six --hand-* — with literal fallbacks so this works headless and
// before the page's CSS loads. We NEVER write to tokens.css / field.css.

// ISLAND PR3 — the electric register speaks the island's ONE motion language
// (motion.ts). Every perpetual beat this module paints — the voice/portrait
// breath, the emission travelling inward, the meeting-mark pulse, a rim answer
// arriving — goes through motion.ts's `loop`/`breathe` helpers, the SAME Motion
// One primitive the overlay drag-release and the swap FLIP already speak. There
// is no second animation system here: the hand-rolled WAAPI (`el.animate`) and
// the island-owned `@keyframes` this module used to inject are RETIRED. The one
// consequence that matters: prefers-reduced-motion is honoured in motion.ts,
// once, for the whole island — a helper called under reduced motion animates
// nothing and the element is left in its resting/settled "met, lit-but-still"
// frame, so this module cannot forget the no-motion path (canon 94 §7 / 83).
import { loop, breathe, prefersReducedMotion } from "./motion.js";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Previously injected the island's own emission `@keyframes` into <head>. The
 * voice/portrait breath now animates through motion.ts (Motion One), which needs
 * no stylesheet, so there is nothing to inject. Kept as a no-op for the one
 * caller (`driveField`) and any external import, so the public surface is
 * unchanged while the second animation system is gone.
 *
 * @deprecated motion.ts owns the island's animation mechanism now.
 */
export function ensureKeyframes(_doc: Document = document): void {
  // intentionally empty — motion.ts (Motion One) drives the breath; no CSS
  // keyframes are injected any more.
}

// The glow filter ids. Emissions, voice cues and the settled meeting mark
// reference them via filter="url(#objexiv-glow)". A single shared filter is the
// "best, not simplest" emission (feGaussianBlur -> merge), not a per-element
// drop-shadow. A hotter variant lights the emission core and the meeting point.
const GLOW_ID = "objexiv-glow";
const GLOW_HOT_ID = "objexiv-glow-hot";

// Fallback charge colours, used only headless / before the page CSS loads. They
// match the reviewed exploration page's amber charge.
const LIVE_FALLBACK = "oklch(0.82 0.17 60)";
const LIVE_HOT_FALLBACK = "oklch(0.95 0.11 75)";
// The MECHANICAL register's cooler charge (canon 94 §2a): a structural signal
// reads as mechanism — cooler and more geometric than the warm human amber. A
// cool desaturated cyan-slate, distinct from the six hands and the warm --live.
// Consumed as var(--live-cool) if the token layer defines it; else this
// fallback. We NEVER write to tokens.css.
const LIVE_COOL_FALLBACK = "oklch(0.80 0.09 215)";
const LIVE_COOL_HOT_FALLBACK = "oklch(0.90 0.06 210)";

// The two registers of the signature (canon 94 §2a). The register SHIFTS WITH
// THE PARTICIPANTS: "human" when a person is speaking (warm, portrait emits),
// "mechanical" when the event is object-to-object with no person speaking (cool,
// geometric, no portrait, no mouth).
export type Register = "human" | "mechanical";

/** A CSS var() reference with the canon-94 fallback baked in. */
function liveColor(): string {
  return `var(--live, ${LIVE_FALLBACK})`;
}
function liveHotColor(): string {
  return `var(--live-hot, ${LIVE_HOT_FALLBACK})`;
}
function coolColor(): string {
  return `var(--live-cool, ${LIVE_COOL_FALLBACK})`;
}
function coolHotColor(): string {
  return `var(--live-cool-hot, ${LIVE_COOL_HOT_FALLBACK})`;
}
/** A hand colour CSS var() for a person's identity (the warm emitter's colour in
 *  the human register). `hand` is one of the six hand names; an unknown/empty
 *  value falls back to the warm --live so the human register is never colourless. */
function handColor(hand: string | null): string {
  const h = (hand ?? "").trim();
  const ok = ["coral", "saffron", "leaf", "teal", "sky", "plum"].includes(h);
  return ok ? `var(--hand-${h}, ${LIVE_FALLBACK})` : liveColor();
}

/** True when the viewer asked for reduced motion. Re-exported from motion.ts —
 *  the island's ONE reader of the media query (motion.ts honours it live for
 *  every helper), re-surfaced here so this module's callers and the demo keep a
 *  stable import. */
export { prefersReducedMotion };

// ---- the emission glow filter (best-form rendering) -------------------------

/**
 * Ensure the shared SVG glow filters exist inside `svg`'s own <defs>. Idempotent:
 * a second call is a no-op. feGaussianBlur produces real light bleed the lit
 * elements merge over themselves — the emission canon 94 §2 asks for, not a flat
 * drop-shadow.
 */
export function ensureGlowFilter(svg: SVGSVGElement): void {
  if (svg.querySelector(`#${GLOW_ID}`)) return;

  let defs = svg.querySelector("defs");
  if (!defs) {
    defs = document.createElementNS(SVG_NS, "defs");
    svg.insertBefore(defs, svg.firstChild);
  }

  defs.appendChild(makeGlow(GLOW_ID, 2.4));
  defs.appendChild(makeGlow(GLOW_HOT_ID, 3.6));
}

function makeGlow(id: string, deviation: number): SVGFilterElement {
  const filter = document.createElementNS(SVG_NS, "filter");
  filter.setAttribute("id", id);
  // Room for the bleed so the glow is not clipped to the element's box.
  filter.setAttribute("x", "-80%");
  filter.setAttribute("y", "-80%");
  filter.setAttribute("width", "260%");
  filter.setAttribute("height", "260%");
  filter.setAttribute("color-interpolation-filters", "sRGB");

  const blur = document.createElementNS(SVG_NS, "feGaussianBlur");
  blur.setAttribute("in", "SourceGraphic");
  blur.setAttribute("stdDeviation", String(deviation));
  blur.setAttribute("result", "blur");

  const merge = document.createElementNS(SVG_NS, "feMerge");
  for (const which of ["blur", "SourceGraphic"]) {
    const node = document.createElementNS(SVG_NS, "feMergeNode");
    node.setAttribute("in", which);
    merge.appendChild(node);
  }

  filter.appendChild(blur);
  filter.appendChild(merge);
  return filter;
}

// ---- geometry: a connection path and its two halves ------------------------

/** A point on the field. */
interface Pt {
  x: number;
  y: number;
}

/** How a connection's two endpoints are expressed. A <line> gives x1/y1/x2/y2;
 *  any other element is driven by an explicit SVG path string (a curved tie). */
type Segment =
  | { kind: "line"; a: Pt; b: Pt }
  | { kind: "path"; d: string; a: Pt; b: Pt };

/** Read a connection element's two endpoints and a path for its geometry. A
 *  <line>'s endpoints are its coords; a data-a / data-b pair of "x,y" overrides
 *  (so a non-line connection can still declare where its two voices sit). */
function connectionSegment(el: Element): Segment | null {
  const a = readPoint(el.getAttribute("data-a"));
  const b = readPoint(el.getAttribute("data-b"));
  if (el.tagName.toLowerCase() === "line") {
    const n = (name: string) => Number(el.getAttribute(name) ?? "0");
    const la = a ?? { x: n("x1"), y: n("y1") };
    const lb = b ?? { x: n("x2"), y: n("y2") };
    return { kind: "line", a: la, b: lb };
  }
  const d = el.getAttribute("d") ?? "";
  if (d && a && b) return { kind: "path", d, a, b };
  return null;
}

/** Parse an "x,y" attribute into a point, or null. */
function readPoint(raw: string | null): Pt | null {
  if (!raw) return null;
  const [sx, sy] = raw.split(",");
  const x = Number(sx);
  const y = Number(sy);
  if (Number.isNaN(x) || Number.isNaN(y)) return null;
  return { x, y };
}

/** The midpoint of a segment — where the two emissions MEET (canon 94 §2). */
function midpoint(seg: Segment): Pt {
  return { x: (seg.a.x + seg.b.x) / 2, y: (seg.a.y + seg.b.y) / 2 };
}

/** The SVG path string for one HALF of the connection: from an endpoint INWARD
 *  to the meeting midpoint. This is the path a single voice's emission travels
 *  (never the whole line — each side only reaches the middle). */
function halfPath(from: Pt, mid: Pt): string {
  return `M ${round2(from.x)} ${round2(from.y)} L ${round2(mid.x)} ${round2(mid.y)}`;
}

// ---- a voice cue (a stylised mouth, never a drawn figure) -------------------

/**
 * Draw a stylised voice cue at an endpoint, aimed toward the meeting point. It
 * is a small opening chevron (two short strokes meeting at the endpoint and
 * opening toward the middle) — crisp charged geometry that reads as "a voice
 * speaking this way", NOT a face or figure (file 83 §1.4). While live it breathes
 * open (the voice-live keyframe); at rest / reduced motion it is simply lit. The
 * cue is appended to the connection's parent and returned so it can be removed.
 */
function voiceCue(
  parent: SVGElement,
  at: Pt,
  toward: Pt,
  color: string,
  reduce: boolean,
): SVGElement {
  // Unit vector from the endpoint toward the middle (the direction the voice
  // speaks), and its perpendicular (the opening of the chevron's two arms).
  const dx = toward.x - at.x;
  const dy = toward.y - at.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;

  const reach = 9; // how far the chevron opens toward the middle
  const spread = 7; // half the mouth's opening width

  // Apex sits just inward of the endpoint; the two arms open toward the middle.
  const apex: Pt = { x: at.x + ux * 2, y: at.y + uy * 2 };
  const arm1: Pt = { x: apex.x + ux * reach + px * spread, y: apex.y + uy * reach + py * spread };
  const arm2: Pt = { x: apex.x + ux * reach - px * spread, y: apex.y + uy * reach - py * spread };

  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("class", "voice-cue" + (reduce ? "" : " voice-live"));

  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute(
    "d",
    `M ${round2(arm1.x)} ${round2(arm1.y)} L ${round2(apex.x)} ${round2(apex.y)} L ${round2(arm2.x)} ${round2(arm2.y)}`,
  );
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", color);
  path.setAttribute("stroke-width", "2");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  path.setAttribute("filter", `url(#${GLOW_ID})`);
  // The voice breathes open through motion.ts (the old @keyframes objexiv-voice,
  // opacity 0.55↔1 over 2.2s). Reduced motion leaves it at its resting opacity:
  // set the lit rest value and let motion.ts's breathe() animate nothing.
  path.style.opacity = reduce ? "1" : "0.55";
  breathe(path, { opacity: 1 }, 2200);
  g.appendChild(path);

  parent.appendChild(g);
  return g;
}

// ---- a portrait cue (the WARM emitter of the human register) ----------------

/**
 * Draw the warm portrait/icon emitter of the HUMAN register (canon 94 §2a): when
 * a PERSON is speaking, their portrait is the emitter, warm and characterful, in
 * their own hand. Every user has a portrait/icon; until a real avatar asset is
 * wired through the render layer, this is a STYLISED portrait token — a filled
 * rounded node (the person) with a warm halo ring and the voice-direction cue —
 * in the person's hand colour. It reads as "a person speaking from this side",
 * NOT a drawn face/figure/cursor (file 83 §1.4): a portrait token is the one
 * stylised cue §2a explicitly allows. If the render layer supplies a real avatar
 * href on the connection (`data-portrait="url"`), it is drawn as an <image>
 * clipped to the node instead of the token — the warm emitter, characterful.
 */
function portraitCue(
  parent: SVGElement,
  at: Pt,
  toward: Pt,
  hand: string,
  reduce: boolean,
  href: string | null,
): SVGElement {
  const color = handColor(hand);
  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("class", "portrait-cue" + (reduce ? "" : " portrait-live"));

  // A warm halo behind the portrait — the warmth of the human register.
  const halo = document.createElementNS(SVG_NS, "circle");
  halo.setAttribute("class", "portrait-halo");
  halo.setAttribute("cx", String(round2(at.x)));
  halo.setAttribute("cy", String(round2(at.y)));
  halo.setAttribute("r", "11");
  halo.setAttribute("fill", "none");
  halo.setAttribute("stroke", color);
  halo.setAttribute("stroke-width", "1.5");
  halo.setAttribute("filter", `url(#${GLOW_ID})`);
  g.appendChild(halo);

  if (href) {
    // A real avatar, clipped to a round node — characterful, the person themself.
    const clipId = `objexiv-portrait-clip-${Math.random().toString(36).slice(2, 9)}`;
    const svg = parent.ownerSVGElement ?? (parent as unknown as SVGSVGElement);
    let defs = svg.querySelector("defs");
    if (!defs) {
      defs = document.createElementNS(SVG_NS, "defs");
      svg.insertBefore(defs, svg.firstChild);
    }
    const clip = document.createElementNS(SVG_NS, "clipPath");
    clip.setAttribute("id", clipId);
    const clipCircle = document.createElementNS(SVG_NS, "circle");
    clipCircle.setAttribute("cx", String(round2(at.x)));
    clipCircle.setAttribute("cy", String(round2(at.y)));
    clipCircle.setAttribute("r", "7.5");
    clip.appendChild(clipCircle);
    defs.appendChild(clip);

    const img = document.createElementNS(SVG_NS, "image");
    img.setAttributeNS("http://www.w3.org/1999/xlink", "href", href);
    img.setAttribute("href", href);
    img.setAttribute("x", String(round2(at.x - 7.5)));
    img.setAttribute("y", String(round2(at.y - 7.5)));
    img.setAttribute("width", "15");
    img.setAttribute("height", "15");
    img.setAttribute("clip-path", `url(#${clipId})`);
    img.setAttribute("preserveAspectRatio", "xMidYMid slice");
    g.appendChild(img);
  } else {
    // The stylised portrait token: a filled node in the person's hand.
    const node = document.createElementNS(SVG_NS, "circle");
    node.setAttribute("class", "portrait-node");
    node.setAttribute("cx", String(round2(at.x)));
    node.setAttribute("cy", String(round2(at.y)));
    node.setAttribute("r", "6.5");
    node.setAttribute("fill", color);
    node.setAttribute("filter", `url(#${GLOW_ID})`);
    g.appendChild(node);
  }

  // The voice-direction cue, tinted to the hand — this person speaks THIS way.
  g.appendChild(voiceCue(g, at, toward, color, reduce));

  // The portrait breathes through motion.ts (the old @keyframes objexiv-portrait,
  // opacity 0.8↔1 over 2.6s). Reduced motion leaves it lit at rest.
  g.style.opacity = reduce ? "1" : "0.8";
  breathe(g, { opacity: 1 }, 2600);
  parent.appendChild(g);
  return g;
}

// ---- an emission travelling inward to the meeting point ---------------------

/**
 * Send one side's emission travelling from its endpoint INWARD to the meeting
 * midpoint — a lit point carrying that side's words toward the centre (canon 94
 * §2). Under reduced motion no travelling point is emitted; the lit meeting mark
 * is the whole signal. The emission is appended to the connection's parent and a
 * handle removes it. `phase` (0..1) offsets the two sides slightly so they read
 * as two voices, not one.
 */
function emit(
  parent: SVGElement,
  from: Pt,
  mid: Pt,
  opts: { durationMs: number; delayMs: number; reduce: boolean; color?: string; glow?: string },
): LiveHandle {
  if (opts.reduce) return noopHandle();

  const dot = document.createElementNS(SVG_NS, "circle");
  dot.setAttribute("class", "emission");
  dot.setAttribute("r", "3.5");
  dot.setAttribute("fill", opts.color ?? liveHotColor());
  dot.setAttribute("filter", `url(#${opts.glow ?? GLOW_HOT_ID})`);
  // offset-path drives the emission along its own half of the connection; where
  // it is unsupported the dot simply stays at the endpoint (still a lit point).
  dot.style.offsetPath = `path("${halfPath(from, mid)}")`;
  (dot.style as unknown as { offsetRotate?: string }).offsetRotate = "0deg";
  parent.appendChild(dot);

  // The emission travels its half of the path FOREVER through motion.ts's loop
  // (the island's one motion language): 0% at the endpoint (the voice), out to
  // 100% at the middle (the meeting), a brief hold, then fade and restart — the
  // words arriving and settling. The per-side phase (delayMs) makes the two
  // voices read as two. offset[] holds the non-uniform stop timing the old WAAPI
  // keyframes carried; repeatType "loop" restarts from the voice each cycle (not
  // a mirror — the words always travel inward). motion.ts short-circuits reduced
  // motion, but emit() already returned a no-op above for that case.
  const anim = loop(
    dot,
    {
      offsetDistance: ["0%", "12%", "88%", "100%", "100%"],
      opacity: [0, 1, 1, 1, 0],
    },
    {
      durationMs: opts.durationMs,
      delayMs: opts.delayMs,
      offset: [0, 0.12, 0.7, 0.82, 1],
      ease: "easeIn",
      repeatType: "loop",
    },
  );

  return {
    clear() {
      anim.cancel();
      dot.remove();
    },
  };
}

// ---- the settled meeting mark (the tie) -------------------------------------

/**
 * Light the meeting point where the two emissions converge (canon 94 §2): a lit
 * mark that settles at the middle of the connection — the formed tie / shared
 * glow. At rest and under reduced motion this IS the signal (lit, still). While
 * live it pulses gently as emissions keep arriving. Appended to the connection's
 * parent; the handle removes it.
 */
function meetingMark(
  parent: SVGElement,
  mid: Pt,
  reduce: boolean,
  opts: { color?: string; glow?: string; register?: Register } = {},
): LiveHandle {
  const register = opts.register ?? "mechanical";
  const mark = document.createElementNS(SVG_NS, "circle");
  mark.setAttribute("class", "meeting meeting-" + register);
  mark.setAttribute("cx", String(round2(mid.x)));
  mark.setAttribute("cy", String(round2(mid.y)));
  // The mechanical meeting is a touch smaller and crisper (mechanism); the human
  // meeting is a touch warmer/larger (the shared thing two people made).
  mark.setAttribute("r", register === "human" ? "4.5" : "4");
  mark.setAttribute("fill", opts.color ?? liveHotColor());
  mark.setAttribute("filter", `url(#${opts.glow ?? GLOW_HOT_ID})`);

  // The meeting pulses FOREVER through motion.ts's loop (one motion language).
  // Human: a warm breathing settle (wider amplitude). Mechanical: a QUIET pulse
  // (canon 94 §2a), tighter amplitude so it reads as mechanism, not warmth. The
  // 3-stop scale/opacity cycle returns to its start, so repeatType "loop" (the
  // default) reads as the same ease-in-out pulse the old WAAPI gave. Under
  // reduced motion motion.ts animates nothing and the mark stays lit at rest.
  mark.style.transformBox = "fill-box";
  mark.style.transformOrigin = "center";
  const human = register === "human";
  const scales = human ? ["scale(0.82)", "scale(1.12)", "scale(0.82)"] : ["scale(0.92)", "scale(1.04)", "scale(0.92)"];
  const opacities = human ? [0.7, 1, 0.7] : [0.65, 0.95, 0.65];
  const anim = loop(
    mark,
    { transform: scales, opacity: opacities },
    { durationMs: human ? 2200 : 2800, ease: "easeInOut" },
  );

  parent.appendChild(mark);
  return {
    clear() {
      anim.cancel();
      mark.remove();
    },
  };
}

// ---- a connection: two voices emitting, meeting in the middle ---------------

/**
 * Light a whole connection as two-sided emission (canon 94 §2 corrected). The
 * resting line keeps its weight (the tie's only state); liveness is painted on
 * top: the line lifts to the charge colour and gains the glow; a stylised voice
 * cue appears at each end; each side emits inward; and a lit mark settles where
 * they MEET in the middle. Returns a handle whose `.clear()` restores the
 * resting line and removes every added element.
 *
 * `voices` controls which sides speak: "both" (a connection forming — both sides
 * speak), or "a"/"b" (a take answering — only the speaking side has a voice, the
 * other end is the challenge/rim the answer lands on).
 *
 * `register` (canon 94 §2a) selects the REGISTER, which shifts with the
 * participants:
 *   - "human": a PERSON is speaking. The warm PORTRAIT in their hand is the
 *     emitter (characterful), the emission and meeting are warm (the hand / the
 *     warm --live), a quiet warm settle. `handA`/`handB` give each speaker's hand
 *     colour; `portraitA`/`portraitB` an optional real avatar href.
 *   - "mechanical" (default): object-to-object, NO person speaking (a take
 *     settling into structure, a projection recomputing, a system-made tie,
 *     activity propagating). NO portrait, NO mouth: cool crisp lights and a quiet
 *     geometric pulse. It is mechanism and reads as mechanism.
 */
export function liveConnection(
  el: SVGLineElement | SVGPathElement,
  opts: {
    reduceMotion?: boolean;
    voices?: "both" | "a" | "b";
    index?: number;
    register?: Register;
    handA?: string | null;
    handB?: string | null;
    portraitA?: string | null;
    portraitB?: string | null;
  } = {},
): LiveHandle {
  const reduce = opts.reduceMotion ?? prefersReducedMotion();
  const svg = el.ownerSVGElement;
  const parent = el.parentNode as SVGElement | null;
  if (svg) ensureGlowFilter(svg);
  if (!parent) return noopHandle();

  const seg = connectionSegment(el);
  if (!seg) return noopHandle();
  const mid = midpoint(seg);
  const voices = opts.voices ?? "both";
  const i = opts.index ?? 0;
  const register: Register = opts.register ?? "mechanical";
  const human = register === "human";

  const prev = {
    stroke: el.style.stroke,
    filter: el.style.filter,
    cls: el.getAttribute("class"),
  };

  // The register's charge colour. HUMAN is warm: the emission colour is the
  // speaker's own hand (identity + warmth), the line a warm --live. MECHANICAL is
  // the cool structural charge. The line's WEIGHT (stroke-width) is left exactly
  // as authored — weight is the tie's only state (file 77).
  const lineColor = human ? liveColor() : coolColor();
  el.classList.add("connection-live", "register-" + register);
  el.style.stroke = lineColor;
  el.style.filter = `url(#${GLOW_ID})`;

  const added: SVGElement[] = [];
  const handles: LiveHandle[] = [];

  const emitColorA = human ? handColor(opts.handA ?? null) : coolHotColor();
  const emitColorB = human ? handColor(opts.handB ?? null) : coolHotColor();
  const emitGlow = human ? GLOW_HOT_ID : GLOW_ID; // mechanical uses the quieter glow

  // The emitters. HUMAN: a warm portrait in the speaker's hand (no bare chevron —
  // the portrait carries the voice cue, tinted to the hand). MECHANICAL: NO
  // portrait and NO mouth — the structural signal has no speaker.
  if (human) {
    if (voices === "both" || voices === "a") {
      added.push(portraitCue(parent, seg.a, mid, opts.handA ?? "", reduce, opts.portraitA ?? null));
    }
    if (voices === "both" || voices === "b") {
      added.push(portraitCue(parent, seg.b, mid, opts.handB ?? "", reduce, opts.portraitB ?? null));
    }
  }

  // The emissions: each speaking side sends its words inward to the middle. A
  // small per-side phase offset makes them read as two voices converging. In the
  // human register they travel in the speaker's hand; mechanical, the cool charge.
  const dur = 1900 + (i % 3) * 180;
  if (voices === "both" || voices === "a") {
    handles.push(emit(parent, seg.a, mid, { durationMs: dur, delayMs: 0, reduce, color: emitColorA, glow: emitGlow }));
  }
  if (voices === "both" || voices === "b") {
    handles.push(emit(parent, seg.b, mid, { durationMs: dur, delayMs: voices === "both" ? dur * 0.12 : 0, reduce, color: emitColorB, glow: emitGlow }));
  }

  // The meeting mark settles where they converge — the formed tie. Warm in the
  // human register, a quiet cool pulse in the mechanical one.
  handles.push(
    meetingMark(parent, mid, reduce, {
      register,
      color: human ? liveHotColor() : coolHotColor(),
      glow: human ? GLOW_HOT_ID : GLOW_ID,
    }),
  );

  return {
    clear() {
      for (const h of handles) h.clear();
      for (const g of added) g.remove();
      el.classList.remove("connection-live", "register-" + register);
      el.style.stroke = prev.stroke;
      el.style.filter = prev.filter;
      if (prev.cls !== null) el.setAttribute("class", prev.cls);
    },
  };
}

// ---- an answer landing on a challenge rim -----------------------------------

/**
 * A take answering a challenge (canon 94 §2): the far object (a point on the
 * rim, where the answer lands) arrives lit as the emission meets it. Under
 * reduced motion it is simply lit at once. The target keeps its own hand colour
 * (the identity of who); liveness only reveals it. Returns a handle that restores
 * the resting opacity.
 */
export function arriveLit(
  el: SVGElement,
  opts: { reduceMotion?: boolean; delayMs?: number; durationMs?: number } = {},
): LiveHandle {
  const reduce = opts.reduceMotion ?? prefersReducedMotion();
  el.classList.add("arrive-lit");
  const prevOpacity = el.style.opacity;

  // The answer arrives lit at rest; while live it pulses as the emission keeps
  // meeting it — through motion.ts's loop (one motion language). Reduced motion:
  // set the end state (fully lit) and let motion.ts animate nothing. The old
  // WAAPI's ease-in-out, non-uniform opacity cadence is carried by offset[].
  el.style.opacity = "1";
  const anim = loop(
    el,
    { opacity: [0.35, 0.35, 1, 1] },
    {
      durationMs: opts.durationMs ?? 1900,
      delayMs: opts.delayMs ?? 0,
      offset: [0, 0.35, 0.7, 1],
      ease: "easeInOut",
      repeatType: "loop",
    },
  );

  return {
    clear() {
      el.classList.remove("arrive-lit");
      anim.cancel();
      el.style.opacity = prevOpacity;
    },
  };
}

// ---- a live challenge ring --------------------------------------------------

/**
 * A live challenge ring (canon 94 §2): the rim carries the quiet lit presence of
 * a challenge that voices are meeting at. The CSS layer already breathes a live
 * rim's glow (field.css .live-rim); the island adds the hot meeting mark at the
 * rim points where answers land, which is done via arriveLit on the rim points.
 * This function lights the ring's own stroke to the charge colour so the ring
 * reads as the shared place the meeting happens, settling to lit at rest. Returns
 * a handle restoring the ring.
 */
export function liveRing(
  ring: SVGCircleElement,
  opts: { reduceMotion?: boolean } = {},
): LiveHandle {
  const svg = ring.ownerSVGElement;
  if (svg) ensureGlowFilter(svg);
  const prev = { stroke: ring.style.stroke, filter: ring.style.filter };
  ring.classList.add("ring-live");
  ring.style.stroke = liveColor();
  ring.style.filter = `url(#${GLOW_ID})`;
  return {
    clear() {
      ring.classList.remove("ring-live");
      ring.style.stroke = prev.stroke;
      ring.style.filter = prev.filter;
    },
  };
}

// ---- handles & helpers ------------------------------------------------------

/** A cleanup handle every live-painting function returns. */
export interface LiveHandle {
  clear(): void;
}

function noopHandle(): LiveHandle {
  return { clear() {} };
}

function round2(f: number): number {
  return Math.round(f * 100) / 100;
}

// ---- the field driver -------------------------------------------------------

/**
 * Drive the electric interaction over a whole field SVG. This is the public
 * entry the island mounts: given the server-emitted field SVG, it discovers the
 * live structure and paints the TWO-SIDED EMISSION circuit (canon 94 §2
 * corrected). It is declarative over data attributes the render layer sets:
 *
 *   - a connection (`line[data-tie]`, or any element with `data-tie`) becomes a
 *     two-sided emission meeting in the middle. `data-voices="a"|"b"` narrows it
 *     to one speaker (a take answering — only the person's side speaks).
 *   - `data-register="human"|"mechanical"` selects the REGISTER (canon 94 §2a).
 *     "human" (a person speaking) draws a warm portrait emitter in the speaker's
 *     hand; `data-hand`/`data-hand-b` give the hand(s) and `data-portrait`/
 *     `data-portrait-b` an optional avatar href. "mechanical" (the default — a
 *     structural signal with no speaker) draws cool crisp lights and a quiet
 *     pulse, NO portrait, NO mouth.
 *   - a `[data-arrive]` element (an answer landing on a rim) arrives lit.
 *   - a `circle.circle[data-live]` (a live challenge ring) lights as the shared
 *     place the meeting happens.
 *
 * `data-live` on a connection is accepted as an alias of `data-tie` so existing
 * server markup keeps working while the render layer migrates. The render layer
 * sets these flags from the server's liveness; the demo harness sets them to
 * prove the effect. drive() returns a handle whose `.clear()` removes all
 * liveness, so an SSE ping that ends liveness can un-paint it.
 */
export function driveField(
  svg: SVGSVGElement,
  opts: { reduceMotion?: boolean } = {},
): LiveHandle {
  const reduce = opts.reduceMotion ?? prefersReducedMotion();
  ensureKeyframes(svg.ownerDocument ?? document);
  ensureGlowFilter(svg);
  const handles: LiveHandle[] = [];

  // Connections: each becomes a two-sided emission meeting in the middle. We
  // accept data-tie (the new name) and data-live (the prior name) on a line so
  // the render layer can migrate without a flag day.
  const connections = svg.querySelectorAll<SVGLineElement | SVGPathElement>(
    "line[data-tie], line[data-live], [data-tie]:not(line)",
  );
  const seen = new Set<Element>();
  connections.forEach((el, i) => {
    if (seen.has(el)) return;
    seen.add(el);
    const v = el.getAttribute("data-voices");
    const voices = v === "a" || v === "b" ? v : "both";
    // The register shifts with the participants (canon 94 §2a). Default
    // MECHANICAL: a connection is a structural signal unless the render layer
    // marks it as a person speaking. Only data-register="human" opts into warmth.
    const register: Register = el.getAttribute("data-register") === "human" ? "human" : "mechanical";
    handles.push(
      liveConnection(el, {
        reduceMotion: reduce,
        voices,
        index: i,
        register,
        handA: el.getAttribute("data-hand"),
        handB: el.getAttribute("data-hand-b") ?? el.getAttribute("data-hand"),
        portraitA: el.getAttribute("data-portrait"),
        portraitB: el.getAttribute("data-portrait-b"),
      }),
    );
  });

  // Answers landing on a rim fade up as the emission meets them.
  svg.querySelectorAll<SVGElement>("[data-arrive]").forEach((el, i) => {
    handles.push(arriveLit(el, { reduceMotion: reduce, delayMs: (i % 3) * 220 }));
  });

  // Live challenge rings: the shared place the meeting happens.
  svg.querySelectorAll<SVGCircleElement>("circle.circle[data-live]").forEach((ring) => {
    handles.push(liveRing(ring, { reduceMotion: reduce }));
  });

  return {
    clear() {
      for (const h of handles) h.clear();
    },
  };
}
